import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Link2, Link2Off, Copy, Check, Shield, ShieldOff,
  Eye, EyeOff, RefreshCw, Clock, AlertTriangle, Loader2,
} from 'lucide-react';
import Modal, { ModalHeader, ModalBody } from '../ui/Modal';
import { shareOwnerApi } from '../../services/share-api';
import type { ShareSettings, ShareStatus } from '../../types/dashboard';

interface ShareSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  dashboardName: string;
}

const REFRESH_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
];

const EXPIRY_OPTIONS = [
  { value: null, label: 'Never' },
  { value: 24, label: '24 hours' },
  { value: 168, label: '7 days' },
  { value: 720, label: '30 days' },
];

const ShareSettingsModal: React.FC<ShareSettingsModalProps> = ({
  isOpen,
  onClose,
  dashboardId,
  dashboardName,
}) => {
  const [status, setStatus] = useState<ShareStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [copied, setCopied] = useState(false);

  // PIN state
  const [showPinInput, setShowPinInput] = useState(false);
  const [pinValue, setPinValue] = useState('');

  // Revoke confirmation
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await shareOwnerApi.getShareStatus(dashboardId);
      setStatus(data);
    } catch {
      toast.error('Failed to load share settings');
    } finally {
      setIsLoading(false);
    }
  }, [dashboardId]);

  useEffect(() => {
    if (isOpen) {
      loadStatus();
      setCopied(false);
      setShowPinInput(false);
      setPinValue('');
      setShowRevokeConfirm(false);
    }
  }, [isOpen, loadStatus]);

  const handleToggleSharing = async () => {
    if (!status) return;
    setIsUpdating(true);
    try {
      if (status.share_enabled) {
        await shareOwnerApi.disableSharing(dashboardId);
        setStatus({ ...status, share_enabled: false });
        toast.success('Sharing disabled');
      } else {
        const updated = await shareOwnerApi.enableSharing(dashboardId);
        setStatus(updated);
        toast.success('Sharing enabled');
      }
    } catch {
      toast.error('Failed to update sharing');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!status?.share_url) return;
    try {
      await navigator.clipboard.writeText(status.share_url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSetPin = async () => {
    if (!/^\d{4,6}$/.test(pinValue)) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    setIsUpdating(true);
    try {
      await shareOwnerApi.setPin(dashboardId, pinValue);
      setStatus(prev => prev ? { ...prev, pin_enabled: true } : prev);
      setShowPinInput(false);
      setPinValue('');
      toast.success('PIN set');
    } catch {
      toast.error('Failed to set PIN');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemovePin = async () => {
    setIsUpdating(true);
    try {
      await shareOwnerApi.removePin(dashboardId);
      setStatus(prev => prev ? { ...prev, pin_enabled: false } : prev);
      toast.success('PIN removed');
    } catch {
      toast.error('Failed to remove PIN');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSettingsChange = async (changes: Partial<ShareSettings>) => {
    setIsUpdating(true);
    try {
      const updated = await shareOwnerApi.updateShareSettings(dashboardId, changes);
      setStatus(prev => prev ? { ...prev, share_settings: updated } : prev);
    } catch {
      toast.error('Failed to update settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRevoke = async () => {
    setIsUpdating(true);
    try {
      const updated = await shareOwnerApi.revokeLink(dashboardId);
      setStatus(updated);
      setShowRevokeConfirm(false);
      toast.success('Link revoked. A new link has been generated.');
    } catch {
      toast.error('Failed to revoke link');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExpiryChange = (hours: number | null) => {
    const expires_at = hours
      ? new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
      : null;
    handleSettingsChange({ expires_at });
  };

  const currentRefresh = status?.share_settings?.auto_refresh_seconds ?? 0;
  const currentExpiry = status?.share_settings?.expires_at;

  const getSelectedExpiryValue = (): number | null => {
    if (!currentExpiry) return null;
    const remaining = new Date(currentExpiry).getTime() - Date.now();
    if (remaining <= 0) return null;
    const hours = remaining / (60 * 60 * 1000);
    if (hours <= 25) return 24;
    if (hours <= 169) return 168;
    return 720;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>
        Share "{dashboardName}"
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : status ? (
          <div className="space-y-6">
            {/* Share toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status.share_enabled ? (
                  <Link2 className="w-5 h-5 text-success" />
                ) : (
                  <Link2Off className="w-5 h-5 text-muted-foreground" />
                )}
                <div>
                  <p className="font-medium text-foreground">
                    {status.share_enabled ? 'Sharing enabled' : 'Sharing disabled'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Anyone with the link can view this dashboard
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleSharing}
                disabled={isUpdating}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  status.share_enabled ? 'bg-success' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    status.share_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {status.share_enabled && status.share_url && (
              <>
                {/* Share link */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Share link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={status.share_url}
                      className="flex-1 rounded-md border border-input bg-muted px-3 py-2 text-sm text-foreground font-mono"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* PIN protection */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {status.pin_enabled ? (
                        <Shield className="w-4 h-4 text-success" />
                      ) : (
                        <ShieldOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium text-foreground">
                        PIN protection
                      </span>
                    </div>
                    {status.pin_enabled ? (
                      <button
                        onClick={handleRemovePin}
                        disabled={isUpdating}
                        className="text-sm text-destructive hover:text-destructive/80"
                      >
                        Remove PIN
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowPinInput(true)}
                        className="text-sm text-primary hover:text-primary/80"
                      >
                        Set PIN
                      </button>
                    )}
                  </div>
                  {status.pin_enabled && (
                    <p className="text-xs text-muted-foreground ml-6">
                      Viewers must enter a PIN to access this dashboard
                    </p>
                  )}
                  {showPinInput && (
                    <div className="flex gap-2 mt-2 ml-6">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={6}
                        placeholder="4-6 digit PIN"
                        value={pinValue}
                        onChange={e => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-32 rounded-md border border-input bg-card px-3 py-1.5 text-sm text-center tracking-widest"
                      />
                      <button
                        onClick={handleSetPin}
                        disabled={isUpdating || pinValue.length < 4}
                        className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
                      >
                        Set
                      </button>
                      <button
                        onClick={() => { setShowPinInput(false); setPinValue(''); }}
                        className="px-3 py-1.5 rounded-md text-muted-foreground text-sm hover:text-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Show controls toggle */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {status.share_settings?.show_controls !== false ? (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium text-foreground">
                        Show controls to viewers
                      </span>
                    </div>
                    <button
                      onClick={() => handleSettingsChange({
                        show_controls: status.share_settings?.show_controls === false,
                      })}
                      disabled={isUpdating}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        status.share_settings?.show_controls !== false
                          ? 'bg-success'
                          : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          status.share_settings?.show_controls !== false
                            ? 'translate-x-[18px]'
                            : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Auto-refresh interval */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        Auto-refresh
                      </span>
                    </div>
                    <select
                      value={currentRefresh}
                      onChange={e => handleSettingsChange({
                        auto_refresh_seconds: Number(e.target.value) || undefined,
                      })}
                      disabled={isUpdating}
                      className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
                    >
                      {REFRESH_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Link expiration */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        Link expires
                      </span>
                    </div>
                    <select
                      value={getSelectedExpiryValue() ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        handleExpiryChange(val);
                      }}
                      disabled={isUpdating}
                      className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
                    >
                      {EXPIRY_OPTIONS.map(opt => (
                        <option key={String(opt.value)} value={opt.value ?? ''}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Revoke link */}
                <div className="border-t border-border pt-4">
                  {showRevokeConfirm ? (
                    <div className="bg-destructive/15 rounded-lg p-3">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-destructive">
                          This will permanently invalidate the current link. A new link will be generated.
                          Anyone using the old link will lose access.
                        </p>
                      </div>
                      <div className="flex gap-2 ml-6">
                        <button
                          onClick={handleRevoke}
                          disabled={isUpdating}
                          className="px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground text-sm hover:bg-destructive/90 disabled:opacity-50 transition-colors"
                        >
                          {isUpdating ? 'Revoking...' : 'Revoke'}
                        </button>
                        <button
                          onClick={() => setShowRevokeConfirm(false)}
                          className="px-3 py-1.5 rounded-md text-muted-foreground text-sm hover:text-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowRevokeConfirm(true)}
                      className="text-sm text-destructive hover:text-destructive/80 transition-colors"
                    >
                      Revoke current link
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            Unable to load share settings
          </p>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ShareSettingsModal;
